import {
  GraphQLClient,
  RequestMiddleware,
  ResponseMiddleware,
} from 'graphql-request';
import { GraphQLClientResponse } from 'graphql-request/build/esm/types';
import { match, P } from 'ts-pattern';
import { EnvironmentService } from '../environment/environment-service';
import { getSdk, Sdk } from '../graphql/generated/graphql-sdk';
import { getAndCreatePanalytHeaders } from '../utilFunctions/requestHeaders';
import { HasDependencies, hashString } from '../v2/api/types';
import { ErrorTracker } from '../v2/common/components/errors/trackers/ErrorTracker';
import { ErrorGroups } from '../v2/common/components/errors/types';
import { AuthToken } from '../v2/context/types';

export class GraphQlRequestService implements HasDependencies {
  public graphQlClient: GraphQLClient;
  public graphQlClientBatching: GraphQLClient;
  public graphQlClientIgnoringErrors: GraphQLClient;

  private errorTracker: ErrorTracker;

  constructor(
    errorTracker: ErrorTracker,
    private readonly token: AuthToken,
    private readonly environmentService: EnvironmentService
  ) {
    this.errorTracker = errorTracker;
    const baseUrl = this.environmentService.getGraphqlUrl();

    const authRequestMiddleware: RequestMiddleware = async (gqlRequest) => {
      if (this.token === undefined) {
        throw new Error(
          'Bearer token undefined when trying to make graphql request.'
        );
      }

      return {
        ...gqlRequest,
        headers: {
          ...gqlRequest.headers,
          authorization: `Bearer ${this.token}`,
          ...getAndCreatePanalytHeaders(),
        },
      };
    };

    const addOpNameToUrlMiddleware: RequestMiddleware = async (gqlRequest) => {
      const operationName = gqlRequest.operationName || 'na';
      const opParam = `op=${encodeURIComponent(operationName)}`;
      let finalUrl = gqlRequest.url;

      if (finalUrl.includes('?')) {
        finalUrl = `${finalUrl}&${opParam}`;
      } else {
        finalUrl = `${finalUrl}?${opParam}`;
      }
      return { ...gqlRequest, url: finalUrl };
    };

    const combinedMiddleware: RequestMiddleware = async (gqlRequest) => {
      let req = await authRequestMiddleware(gqlRequest);
      req = await addOpNameToUrlMiddleware(req);
      return req;
    };

    const responseMiddleware: ResponseMiddleware = (
      resOrError: GraphQLClientResponse<unknown> | Error
    ): void => {
      match(resOrError)
        .with({ errors: P.any }, (r: GraphQLClientResponse<unknown>) => {
          if (r.errors && r.errors.length === 1) {
            const error = r.errors[0];
            const errorCode = error?.extensions?.errorCode;
            const errorReference = error?.extensions?.errorReference;
            this.errorTracker.trackError({
              error,
              tags: {
                'panalyt.errorGroup': ErrorGroups.API,
                statusCode: r.status,
                errorCode,
                errorReference,
              },
            });
          } else if (r.errors) {
            const errorMessages = r.errors.map((e) => e?.message);
            const errorCodes = r.errors.map((e) => e?.extensions?.errorCode);
            const errorReferences = r.errors.map(
              (e) => e?.extensions?.errorReference
            );
            this.errorTracker.trackError({
              error: new Error(errorMessages?.join(',')),
              tags: {
                'panalyt.errorGroup': ErrorGroups.API,
                statusCode: r.status,
                errorCodes,
                errorReferences,
              },
            });
          } else {
            this.errorTracker.trackError({
              error: new Error('HTTP status 400 without error message'),
              tags: {
                'panalyt.errorGroup': ErrorGroups.API,
                statusCode: r.status,
              },
            });
          }
        })
        .otherwise(() => {
          // Do nothing the response is fine
        });
    };

    const commonClientOptions = {
      headers: {
        'X-PANALYT-CSRF-PROTECTION':
          environmentService.getCSRFProtectionToken(),
      },
      responseMiddleware,
    };

    this.graphQlClient = new GraphQLClient(baseUrl, {
      ...commonClientOptions,
      requestMiddleware: combinedMiddleware,
    });

    this.graphQlClientBatching = new GraphQLClient(`${baseUrl}?batching=true`, {
      ...commonClientOptions,
      requestMiddleware: authRequestMiddleware, // Only auth for batching client
    });

    this.graphQlClientIgnoringErrors = new GraphQLClient(baseUrl, {
      ...commonClientOptions,
      requestMiddleware: combinedMiddleware,
      errorPolicy: 'ignore',
    });
  }

  public updateErrorTracker = (errorTracker: ErrorTracker) => {
    this.errorTracker = errorTracker;
  };

  public getDeps = (): (string | undefined)[] => [
    hashString([
      ...this.errorTracker.getDeps(),
      ...this.environmentService.getDeps(),
    ]),
  ];

  public get graphQlSdk(): Sdk {
    if (!this.graphQlClient) {
      throw new Error('GraphQl client not initialized');
    }
    return getSdk(this.graphQlClient);
  }

  // Use this one if you are okay with receiving only partial results for a query
  public get graphQlSdkIgnoringErrors(): Sdk {
    if (!this.graphQlClientIgnoringErrors) {
      throw new Error('GraphQlIgnoringErrors client not initialized');
    }
    return getSdk(this.graphQlClientIgnoringErrors);
  }
}
